import { useState, useEffect } from "react";
import { api } from "@/api/client";
import { X, Clock, User, FileText, Calendar } from "lucide-react";

export default function ActivityLogsModal({ onClose }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilter, setDateFilter] = useState("");

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      const data = await api.entities.ActivityLog.list('-created_at', 200);
      setLogs(data);
    } catch (err) {
      console.error("Failed to load activity logs:", err);
    } finally {
      setLoading(false);
    }
  };

  const filteredLogs = logs.filter(log => {
    const search = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm || 
      log.order_number?.toLowerCase().includes(search) ||
      log.mailing_name?.toLowerCase().includes(search) ||
      log.description?.toLowerCase().includes(search) ||
      log.actor_name?.toLowerCase().includes(search) ||
      log.actor_email?.toLowerCase().includes(search);
    
    const matchesDate = !dateFilter || 
      new Date(log.created_at).toISOString().split('T')[0] === dateFilter;
    
    return matchesSearch && matchesDate;
  });

  const groupedLogs = filteredLogs.reduce((acc, log) => {
    const date = new Date(log.created_at).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(log);
    return acc;
  }, {});

  return (
    <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "rgba(0, 0, 0, 0.5)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
      padding: "1rem"
    }}>
      <div style={{
        background: "white",
        borderRadius: "12px",
        width: "95%",
        maxWidth: "900px",
        maxHeight: "90vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden"
      }}>
        {/* Header */}
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "1.5rem",
          borderBottom: "2px solid #e2e8f0"
        }}>
          <h2 style={{ fontSize: "1.5rem", fontWeight: 700, color: "#1e293b" }}>
            Activity Logs
          </h2>
          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              color: "#64748b",
              padding: "0.5rem"
            }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Filters */}
        <div style={{
          padding: "1rem 1.5rem",
          background: "#f8fafc",
          borderBottom: "1px solid #e2e8f0",
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "1rem"
        }}>
          <input
            type="text"
            className="form-input"
            placeholder="Search by order, mailing name, or user..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ fontSize: "0.875rem" }}
          />
          <input
            type="date"
            className="form-input"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            style={{ fontSize: "0.875rem" }}
          />
        </div>

        {/* Logs Content */}
        <div style={{
          flex: 1,
          overflowY: "auto",
          padding: "1.5rem"
        }}>
          {loading ? (
            <div style={{ textAlign: "center", padding: "3rem", color: "#64748b" }}>
              Loading activity logs...
            </div>
          ) : filteredLogs.length === 0 ? (
            <div style={{ textAlign: "center", padding: "3rem", color: "#64748b" }}>
              <FileText size={48} style={{ margin: "0 auto 1rem", opacity: 0.5 }} />
              <p>No activity logs found</p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
              {Object.entries(groupedLogs).map(([date, dateLogs]) => (
                <div key={date}>
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    marginBottom: "1rem",
                    color: "#3b82f6",
                    fontWeight: 600,
                    fontSize: "0.875rem"
                  }}>
                    <Calendar size={16} />
                    {date}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                    {dateLogs.map((log) => (
                      <div
                        key={log.id}
                        style={{
                          background: "#f8fafc",
                          padding: "1rem",
                          borderRadius: "8px",
                          borderLeft: "4px solid #3b82f6"
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.5rem" }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: "0.875rem", fontWeight: 600, color: "#1e293b", marginBottom: "0.25rem" }}>
                              {log.mailing_name} - Order {log.order_number}
                            </div>
                            <div style={{ fontSize: "0.875rem", color: "#475569" }}>
                              {log.description}
                            </div>
                          </div>
                          <div style={{
                            fontSize: "0.75rem",
                            color: "#64748b",
                            display: "flex",
                            alignItems: "center",
                            gap: "0.25rem",
                            whiteSpace: "nowrap"
                          }}>
                            <Clock size={12} />
                            {new Date(log.created_at).toLocaleTimeString('en-US', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                        </div>
                        <div style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.25rem",
                          fontSize: "0.75rem",
                          color: "#64748b"
                        }}>
                          <User size={12} />
                          {log.actor_name || log.actor_email || 'Unknown'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}